import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence, MotionConfig } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';

const EASE: [number, number, number, number] = [0.22, 1, 0.36, 1];

const SignUp: React.FC = () => {
  const navigate = useNavigate();
  const { signUp } = useAuth();

  const [formData, setFormData] = useState({
    email: '',
    username: '',
    password: '',
    confirmPassword: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);

  const calculatePasswordStrength = (password: string) => {
    let strength = 0;
    if (password.length >= 8) strength++;
    if (password.length >= 12) strength++;
    if (/[a-z]/.test(password) && /[A-Z]/.test(password)) strength++;
    if (/\d/.test(password)) strength++;
    if (/[^a-zA-Z\d]/.test(password)) strength++;
    return strength;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    if (formData.username.length < 3) {
      setError('Username must be at least 3 characters');
      return;
    }
    if (!/^[a-zA-Z0-9_]+$/.test(formData.username)) {
      setError('Username can only contain letters, numbers, and underscores');
      return;
    }

    setLoading(true);
    const { error: signUpError, needsEmailConfirmation } = await signUp(
      formData.email,
      formData.password,
      formData.username,
    );

    if (signUpError) {
      setError(signUpError.message);
      setLoading(false);
    } else if (needsEmailConfirmation) {
      navigate('/confirm-email', { state: { email: formData.email } });
    } else {
      // Email autoconfirm is on — the user already has a session.
      navigate('/arena');
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (name === 'password') setPasswordStrength(calculatePasswordStrength(value));
  };

  const strengthLabel = passwordStrength <= 1 ? 'Weak' : passwordStrength <= 3 ? 'Medium' : 'Strong';
  const strengthWidth = `${(passwordStrength / 5) * 100}%`;
  const strengthOpacity =
    passwordStrength <= 1 ? 'bg-charcoal/30' : passwordStrength <= 3 ? 'bg-charcoal/60' : 'bg-charcoal';

  const inputClass =
    'w-full rounded-lg border border-black/[0.08] bg-white px-4 py-3 text-sm text-charcoal placeholder-ink-muted/60 transition-colors focus:border-charcoal/30 focus:outline-none focus:ring-2 focus:ring-charcoal/10';

  return (
    <MotionConfig reducedMotion="user">
      <div className="relative isolate flex min-h-screen w-full items-center justify-center bg-white px-6 py-16">
        {/* Blueprint grid */}
        <div aria-hidden className="blueprint-grid pointer-events-none absolute inset-0 z-0" />

        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55, ease: EASE }}
          className="relative z-10 w-full max-w-sm"
        >
          {/* Header */}
          <div className="mb-8 text-center">
            <span className="inline-block text-xs font-semibold uppercase tracking-eyebrow text-ink-muted">
              Steelman
            </span>
            <h1 className="mt-3 font-serif text-4xl tracking-tight text-charcoal">
              Create an account.
            </h1>
            <p className="mt-2 text-sm text-ink-muted">
              Start with 3 free debate credits.
            </p>
          </div>

          {/* Form card */}
          <div className="rounded-2xl border border-black/[0.07] bg-white/90 p-8 shadow-frame backdrop-blur-sm">
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Error */}
              <AnimatePresence>
                {error && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.25, ease: EASE }}
                    className="overflow-hidden rounded-lg border border-black/[0.08] bg-charcoal/[0.04] px-4 py-3 text-sm text-charcoal"
                  >
                    {error}
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Email */}
              <div>
                <label htmlFor="email" className="mb-1.5 block text-xs font-semibold uppercase tracking-eyebrow text-ink-muted">
                  Email
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  placeholder="you@example.com"
                  className={inputClass}
                />
              </div>

              {/* Username */}
              <div>
                <label htmlFor="username" className="mb-1.5 block text-xs font-semibold uppercase tracking-eyebrow text-ink-muted">
                  Username
                </label>
                <input
                  type="text"
                  id="username"
                  name="username"
                  value={formData.username}
                  onChange={handleChange}
                  required
                  minLength={3}
                  maxLength={20}
                  pattern="[a-zA-Z0-9_]+"
                  placeholder="your_handle"
                  className={inputClass}
                />
                <p className="mt-1.5 text-xs text-ink-muted">
                  3–20 characters · letters, numbers, underscores
                </p>
              </div>

              {/* Password */}
              <div>
                <label htmlFor="password" className="mb-1.5 block text-xs font-semibold uppercase tracking-eyebrow text-ink-muted">
                  Password
                </label>
                <input
                  type="password"
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  minLength={8}
                  placeholder="••••••••"
                  className={inputClass}
                />
                {formData.password && (
                  <div className="mt-2">
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-ink-muted">Strength</span>
                      <span className="text-xs font-medium text-charcoal">{strengthLabel}</span>
                    </div>
                    <div className="mt-1 h-1 w-full overflow-hidden rounded-full bg-hairline">
                      <div
                        className={`h-full rounded-full transition-all duration-300 ${strengthOpacity}`}
                        style={{ width: strengthWidth }}
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Confirm Password */}
              <div>
                <label htmlFor="confirmPassword" className="mb-1.5 block text-xs font-semibold uppercase tracking-eyebrow text-ink-muted">
                  Confirm password
                </label>
                <input
                  type="password"
                  id="confirmPassword"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  required
                  placeholder="••••••••"
                  className={inputClass}
                />
              </div>

              {/* Submit */}
              <button
                type="submit"
                disabled={loading}
                className="btn-primary mt-1 w-full disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:translate-y-0 disabled:hover:shadow-none"
              >
                {loading ? 'Creating account…' : 'Create account'}
              </button>
            </form>
          </div>

          {/* Sign in link */}
          <p className="mt-6 text-center text-sm text-ink-muted">
            Already have an account?{' '}
            <Link
              to="/signin"
              className="font-semibold text-charcoal transition-colors hover:text-ink-muted"
            >
              Sign in
            </Link>
          </p>
        </motion.div>
      </div>
    </MotionConfig>
  );
};

export default SignUp;
